import React, { useState, useRef, useEffect } from 'react';
import { useDiscountApproval } from '../../contexts/DiscountApprovalContext';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';

const DiscountApprovalModal = () => {
  const {
    isModalOpen,
    pendingDiscount,
    otpSent,
    otpCode,
    isVerifying,
    isRequestingOTP,
    closeModal,
    requestDiscountApproval,
    verifyOTP,
    setOtpCode
  } = useDiscountApproval();

  const [reason, setReason] = useState('');
  const reasonTextareaRef = useRef(null);
  const sendOtpButtonRef = useRef(null);
  const otpInputRef = useRef(null);
  const verifyOtpButtonRef = useRef(null);

  // Auto-focus the Send OTP button when modal opens
  useEffect(() => {
    if (isModalOpen && !otpSent && sendOtpButtonRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        sendOtpButtonRef.current.focus();
      }, 100);
    }
  }, [isModalOpen, otpSent]);

  // Auto-focus the OTP input when OTP is sent successfully
  useEffect(() => {
    if (isModalOpen && otpSent && otpInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        otpInputRef.current.focus();
      }, 100);
    }
  }, [isModalOpen, otpSent]);

  const handleRequestOTP = async () => {
    try {
      await requestDiscountApproval({
        ...pendingDiscount,
        reason: reason.trim() || null
      });
    } catch (error) {
      // Error is handled in the context
    }
  };

  const handleVerifyOTP = async () => {
    await verifyOTP();
  };

  const handleCancel = () => {
    closeModal(true, reason); // Pass true to indicate rejection
  };

  if (!isModalOpen || !pendingDiscount) {
    return null;
  }

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleCancel}
      title="Discount Approval Required"
    >
      <div className="space-y-6">
        {/* Discount Information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <h3 className="font-semibold text-yellow-800">Discount Limit Exceeded</h3>
          </div>
          <p className="text-yellow-700 text-sm">
            You are requesting a discount of <strong>{pendingDiscount.percentage}%</strong> which exceeds your allowed limit.
          </p>
          {pendingDiscount.amount && (
            <p className="text-yellow-700 text-sm mt-1">
              Amount: <strong>${pendingDiscount.amount.toFixed(2)}</strong>
            </p>
          )}
        </div>

        {!otpSent ? (
          /* Request OTP Section */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Discount <span className="text-gray-400">(Optional)</span>
              </label>
              <textarea
                ref={reasonTextareaRef}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a reason for this discount (optional)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {reason.length}/500 characters
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-blue-800">OTP Verification Required</h4>
                  <p className="text-blue-700 text-sm mt-1">
                    An OTP will be sent to the configured notification email for approval.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isRequestingOTP}
              >
                Cancel
              </Button>
              <Button
                ref={sendOtpButtonRef}
                onClick={handleRequestOTP}
                disabled={isRequestingOTP}
              >
                {isRequestingOTP ? 'Sending...' : 'Send OTP'}
              </Button>
            </div>
          </div>
        ) : (
          /* Verify OTP Section */
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h4 className="font-medium text-green-800">OTP Sent Successfully</h4>
              </div>
              <p className="text-green-700 text-sm mt-1">
                Please check the notification email and enter the OTP code below.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter OTP Code
              </label>
              <Input
                ref={otpInputRef}
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && otpCode.trim() && !isVerifying) {
                    e.preventDefault();
                    handleVerifyOTP();
                  }
                }}
                placeholder="Enter 6-digit OTP code"
                maxLength={8}
                className="text-center text-lg tracking-widest"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isVerifying}
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerifyOTP}
                disabled={!otpCode.trim() || isVerifying}
              >
                {isVerifying ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DiscountApprovalModal;

